/**
 * ERGO 360 Quiz - 交互式问卷组件
 *
 * 动画效果（使用 GSAP）：
 * 1. 标题从下淡入飘入
 * 2. 副标题从下淡入飘入（标题快结束时开始）
 * 3. 问题卡片从下淡入飘入
 */

class Ergo360Quiz {
  constructor(container) {
    this.container = container;
    this.currentStep = 1;
    this.totalSteps = 5;
    this.answers = {};
    this.isAnimating = false;
    this.hasAnimated = false;
    this.restoredFromStorage = false; // 是否从 localStorage 恢复（用于控制入场动画）

    // Card 5 内部状态：'input' | 'result' | 'success'
    this.step5State = 'input';

    // 单位系统：'metric' | 'imperial'
    this.unitSystem = 'imperial';

    // 换算常量
    this.CONVERSIONS = {
      CM_TO_FT: 1 / 30.48,
      FT_TO_CM: 30.48,
      KG_TO_LBS: 2.20462,
      LBS_TO_KG: 1 / 2.20462
    };

    // 动画配置
    this.cardTransitionDelay = 500; // 选择后跳转延迟 (ms)

    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.loadResultsData();
    this.setupIntersectionObserver();
    this.restoreFromLocalStorage();
  }

  // ===== localStorage 存储 =====

  saveToLocalStorage() {
    // 获取完整的结果数据（icon, label, description）
    const resultData = this.resultsData && this.currentResult
      ? this.resultsData[this.currentResult]
      : null;

    const data = {
      answers: this.answers,
      result: this.currentResult,
      resultData: resultData, // 存储完整结果信息供其他页面使用
      timestamp: Date.now()
    };
    localStorage.setItem('ergo360QuizData', JSON.stringify(data));

    // 触发自定义事件，通知其他组件（如 product section）Quiz 已完成
    document.dispatchEvent(new CustomEvent('ergo360QuizComplete', {
      detail: data
    }));
  }

  clearLocalStorage() {
    localStorage.removeItem('ergo360QuizData');
  }

  restoreFromLocalStorage() {
    const savedData = localStorage.getItem('ergo360QuizData');
    if (!savedData) return;

    try {
      const data = JSON.parse(savedData);

      // 检查数据是否有效（可选：添加过期时间检查，比如 7 天）
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > sevenDays) {
        this.clearLocalStorage();
        return;
      }

      // 恢复数据
      this.answers = data.answers || {};
      this.currentResult = data.result;

      // 直接跳转到 Card 5 的 result 状态
      this.currentStep = 5;

      // 设置 track 位置（无动画）
      const offset = (5 - 1) * 100;
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(-${offset}%)`;

      // 显示 Card 5
      this.cards.forEach((card, index) => {
        card.classList.add('is-visible');
      });

      // 显示结果
      if (this.currentResult) {
        this.displayResult(this.currentResult);
      }

      // 切换到 result 状态（直接设置，不用动画）
      this.step5InputState.classList.add('yd-hidden');
      this.step5ResultState.classList.remove('yd-hidden');
      this.step5State = 'result';

      // 更新进度条
      this.updateProgress(5);

      // 恢复 track 动画
      requestAnimationFrame(() => {
        this.track.style.transition = '';
      });

      // 标记从 localStorage 恢复（入场动画只播放标题，跳过卡片）
      this.restoredFromStorage = true;

    } catch (e) {
      this.clearLocalStorage();
    }
  }

  cacheElements() {
    this.title = this.container.querySelector('.ergo-360-quiz__title');
    this.subtitle = this.container.querySelector('.ergo-360-quiz__subtitle');
    this.track = this.container.querySelector('.ergo-360-quiz__track');
    this.cards = this.container.querySelectorAll('.ergo-360-quiz__card');
    this.options = this.container.querySelectorAll('.ergo-360-quiz__option');
    this.backButtons = this.container.querySelectorAll('.ergo-360-quiz__back');
    this.skipButtons = this.container.querySelectorAll('.ergo-360-quiz__skip');
    this.nextButton = this.container.querySelector('.ergo-360-quiz__next');
    this.submitButton = this.container.querySelector('.ergo-360-quiz__submit');
    // 桌面端输入框
    this.heightInput = this.container.querySelector('[data-input="height"]');
    this.weightInput = this.container.querySelector('[data-input="weight"]');
    // 移动端输入框
    this.heightInputMobile = this.container.querySelector('[data-input="height-mobile"]');
    this.weightInputMobile = this.container.querySelector('[data-input="weight-mobile"]');
    this.emailInput = this.container.querySelector('[data-input="email"]');

    // Card 5 内部状态容器
    this.step5InputState = this.container.querySelector('.ergo-360-quiz__step5-input');
    this.step5ResultState = this.container.querySelector('.ergo-360-quiz__step5-result');
    this.step5SuccessState = this.container.querySelector('.ergo-360-quiz__step5-success');

    // 单位切换器
    this.unitToggleBtns = this.container.querySelectorAll('.ergo-360-quiz__unit-btn');
    this.heightUnitLabels = this.container.querySelectorAll('[data-unit-label="height"]');
    this.weightUnitLabels = this.container.querySelectorAll('[data-unit-label="weight"]');

    // Brevo 配置（通过代理调用，API Key 不暴露在前端）
    this.brevoProxyUrl = this.container.dataset.brevoProxyUrl || '';
    this.brevoListId = this.container.dataset.brevoListId || '';
  }

  bindEvents() {
    // 选项点击
    this.options.forEach((option) => {
      option.addEventListener('click', (e) => this.handleOptionClick(e));
    });

    // Back 按钮
    this.backButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.goBack());
    });

    // Skip 按钮（可能有多个）
    this.skipButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.skipToResult());
    });

    // Next 按钮（Generate Report）- 需要验证输入
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.handleGenerateReport());
    }

    // Submit 按钮
    if (this.submitButton) {
      this.submitButton.addEventListener('click', () => this.submitEmail());
    }

    // 单位切换按钮
    this.unitToggleBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleUnitToggle(e));
    });
  }

  loadResultsData() {
    const dataScript = document.getElementById('ergo-360-quiz-results');
    if (dataScript) {
      try {
        this.resultsData = JSON.parse(dataScript.textContent);
      } catch (e) {
        this.resultsData = {};
      }
    }
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimated) {
            this.hasAnimated = true;
            this.playEntryAnimation();
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -50% 0px', // 当 section 顶边进入视窗 50% 位置时触发
      }
    );

    observer.observe(this.container);
  }

  // ===== 进场动画（GSAP）=====

  playEntryAnimation() {
    // 等待 GSAP 加载
    if (typeof gsap === 'undefined') {
      setTimeout(() => this.playEntryAnimation(), 100);
      return;
    }

    // 创建动画时间线
    const tl = gsap.timeline();

    // 1. 标题从下淡入
    if (this.title) {
      tl.fromTo(
        this.title,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
      );
    }

    // 2. 副标题从下淡入（标题快结束时开始）
    if (this.subtitle) {
      tl.fromTo(
        this.subtitle,
        { opacity: 0, y: 20 },
        { opacity: 0.6, y: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.4' // 提前 0.4s 开始
      );
    }

    // 3. 如果是从 localStorage 恢复的，跳过卡片动画（卡片已经在 Card 5 显示结果了）
    if (this.restoredFromStorage) {
      return;
    }

    // 4. 第一个问题卡片从下淡入（仅正常流程时）
    const firstCard = this.cards[0];
    if (firstCard) {
      tl.fromTo(
        firstCard,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          onComplete: () => {
            firstCard.classList.add('is-visible');
          }
        },
        '-=0.3' // 提前开始
      );
    }
  }

  // ===== 交互逻辑 =====

  handleOptionClick(e) {
    if (this.isAnimating) return;

    const option = e.currentTarget;
    const step = parseInt(option.dataset.step);
    const value = option.dataset.value;  // 数字，用于计算
    const text = option.dataset.text;    // 文字，用于邮件传参

    // 保存答案
    this.answers[`step${step}`] = value;
    this.answers[`step${step}Text`] = text;

    // GA4 埋点：Quiz 步骤完成
    if (window.Ergo360Analytics) {
      // 第一题选择时，触发 quiz_start
      if (step === 1 && !this.hasStartedQuiz) {
        this.hasStartedQuiz = true;
        window.Ergo360Analytics.quizStart();
      }
      // 每步选择完成
      window.Ergo360Analytics.quizStepComplete(step, text || value);
    }

    // 更新选中状态
    this.updateOptionSelection(step, value);

    // 延迟后跳转到下一步
    this.isAnimating = true;
    setTimeout(() => {
      this.goToNextStep(step);
      this.isAnimating = false;
    }, this.cardTransitionDelay);
  }

  updateOptionSelection(step, value) {
    // 清除同一步骤的其他选中状态
    const stepOptions = this.container.querySelectorAll(
      `.ergo-360-quiz__option[data-step="${step}"]`
    );
    stepOptions.forEach((opt) => {
      opt.removeAttribute('data-selected');
    });

    // 设置当前选中
    const selectedOptions = this.container.querySelectorAll(
      `.ergo-360-quiz__option[data-step="${step}"][data-value="${value}"]`
    );
    selectedOptions.forEach((opt) => {
      opt.setAttribute('data-selected', 'true');
    });
  }

  goToNextStep(currentStep) {
    if (currentStep < this.totalSteps) {
      this.goToStep(currentStep + 1);
    } else {
      this.goToResult();
    }
  }

  goToStep(step) {
    this.currentStep = step;

    // 计算偏移量
    const offset = (step - 1) * 100;
    this.track.style.transform = `translateX(-${offset}%)`;

    // 显示目标卡片
    const targetCard = this.cards[step - 1];
    if (targetCard) {
      targetCard.classList.add('is-visible');
    }

    // 更新进度条
    this.updateProgress(step);
  }

  goBack() {
    if (this.isAnimating) return;

    // 如果在 Card 5 且不是 input 状态（result 或 success），重置整个问卷
    if (this.currentStep === 5 && this.step5State !== 'input') {
      this.resetQuiz();
      return;
    }

    // GA4 埋点：Quiz 返回上一步
    if (window.Ergo360Analytics && this.currentStep > 1) {
      window.Ergo360Analytics.quizAbandon(this.currentStep);
    }

    // 普通问题步骤返回
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }

  // ===== 重置问卷 =====

  resetQuiz() {
    // 清空 localStorage
    this.clearLocalStorage();

    // 重置状态
    this.answers = {};
    this.currentResult = null;
    this.currentStep = 1;
    this.step5State = 'input';

    // 清除所有选项的选中状态
    this.options.forEach((opt) => {
      opt.removeAttribute('data-selected');
    });

    // 清空输入框
    if (this.heightInput) this.heightInput.value = '';
    if (this.weightInput) this.weightInput.value = '';
    if (this.heightInputMobile) this.heightInputMobile.value = '';
    if (this.weightInputMobile) this.weightInputMobile.value = '';
    if (this.emailInput) this.emailInput.value = '';

    // 重置提交按钮状态
    if (this.submitButton) {
      this.submitButton.disabled = false;
      this.submitButton.textContent = 'Receive full report';
    }

    // 重置单位切换器为公制（默认）
    if (this.unitSystem !== 'metric') {
      this.unitSystem = 'metric';
      this.unitToggleBtns.forEach((btn) => {
        if (btn.dataset.unit === 'metric') {
          btn.setAttribute('data-selected', '');
        } else {
          btn.removeAttribute('data-selected');
        }
      });
      this.updateUnitLabels('metric');
      this.updatePlaceholders('metric');
    }

    // 重置 Card 5 内部状态显示
    this.step5InputState.classList.remove('yd-hidden');
    this.step5ResultState.classList.add('yd-hidden');
    this.step5SuccessState.classList.add('yd-hidden');

    // 滑动回第一个 card
    this.track.style.transform = 'translateX(0%)';

    // 更新进度条
    this.updateProgress(1);
  }

  skipToResult() {
    // GA4 埋点：Quiz 跳过
    if (window.Ergo360Analytics) {
      window.Ergo360Analytics.quizSkip(this.currentStep);
    }

    // 跳过身高体重输入，直接到结果
    this.goToResult();
  }

  handleGenerateReport() {
    // 验证身高和体重是否都已填写
    const heightValue = this.heightInput?.value || this.heightInputMobile?.value;
    const weightValue = this.weightInput?.value || this.weightInputMobile?.value;

    // 如果没有填写完整，不执行任何操作
    if (!heightValue || !weightValue) {
      return;
    }

    // 填写完整，跳转到结果
    this.goToResult();
  }

  // ===== 单位切换 =====

  handleUnitToggle(e) {
    const targetUnit = e.currentTarget.dataset.unit;
    if (targetUnit === this.unitSystem) return;

    // 更新所有切换按钮的选中状态
    this.unitToggleBtns.forEach((btn) => {
      if (btn.dataset.unit === targetUnit) {
        btn.setAttribute('data-selected', '');
      } else {
        btn.removeAttribute('data-selected');
      }
    });

    // 换算当前输入值
    this.convertInputValues(this.unitSystem, targetUnit);

    // 更新单位标签
    this.updateUnitLabels(targetUnit);

    // 更新 placeholder
    this.updatePlaceholders(targetUnit);

    // 保存当前单位
    this.unitSystem = targetUnit;
  }

  convertInputValues(fromUnit, toUnit) {
    const heightInputs = [this.heightInput, this.heightInputMobile];
    const weightInputs = [this.weightInput, this.weightInputMobile];

    heightInputs.forEach((input) => {
      if (input && input.value) {
        const value = parseFloat(input.value);
        if (fromUnit === 'metric' && toUnit === 'imperial') {
          // CM → FT
          input.value = (value * this.CONVERSIONS.CM_TO_FT).toFixed(2);
        } else {
          // FT → CM
          input.value = Math.round(value * this.CONVERSIONS.FT_TO_CM);
        }
      }
    });

    weightInputs.forEach((input) => {
      if (input && input.value) {
        const value = parseFloat(input.value);
        if (fromUnit === 'metric' && toUnit === 'imperial') {
          // KG → LBS
          input.value = (value * this.CONVERSIONS.KG_TO_LBS).toFixed(2);
        } else {
          // LBS → KG
          input.value = Math.round(value * this.CONVERSIONS.LBS_TO_KG);
        }
      }
    });
  }

  updateUnitLabels(unitSystem) {
    const heightUnit = unitSystem === 'metric' ? 'CM' : 'FT';
    const weightUnit = unitSystem === 'metric' ? 'KG' : 'LB';

    this.heightUnitLabels.forEach((label) => {
      label.textContent = heightUnit;
    });
    this.weightUnitLabels.forEach((label) => {
      label.textContent = weightUnit;
    });
  }

  updatePlaceholders(unitSystem) {
    const heightPlaceholder = unitSystem === 'metric' ? '180' : '5.91';
    const weightPlaceholder = unitSystem === 'metric' ? '80' : '176';

    if (this.heightInput) this.heightInput.placeholder = heightPlaceholder;
    if (this.heightInputMobile) this.heightInputMobile.placeholder = heightPlaceholder;
    if (this.weightInput) this.weightInput.placeholder = weightPlaceholder;
    if (this.weightInputMobile) this.weightInputMobile.placeholder = weightPlaceholder;
  }

  goToResult() {
    // 收集身高体重数据（桌面端或移动端）
    const heightValue = this.heightInput?.value || this.heightInputMobile?.value;
    const weightValue = this.weightInput?.value || this.weightInputMobile?.value;

    // 统一转换为公制单位存储（CM/KG）
    if (heightValue) {
      let height = parseFloat(heightValue);
      if (this.unitSystem === 'imperial') {
        // FT → CM
        height = Math.round(height * this.CONVERSIONS.FT_TO_CM);
      } else {
        height = Math.round(height);
      }
      this.answers.height = height;
    }
    if (weightValue) {
      let weight = parseFloat(weightValue);
      if (this.unitSystem === 'imperial') {
        // LBS → KG
        weight = Math.round(weight * this.CONVERSIONS.LBS_TO_KG);
      } else {
        weight = Math.round(weight);
      }
      this.answers.weight = weight;
    }

    // 计算结果
    const result = this.calculateResult();
    this.currentResult = result;
    this.displayResult(result);

    // 保存到 localStorage
    this.saveToLocalStorage();

    // GA4 埋点：Quiz 完成
    if (window.Ergo360Analytics) {
      window.Ergo360Analytics.quizComplete(
        result,
        this.answers.height,
        this.answers.weight,
        this.unitSystem
      );
    }

    // 切换到结果状态（Card 5 内部状态切换）
    this.showStep5State('result');
  }

  // ===== Card 5 内部状态切换 =====

  showStep5State(targetState) {
    if (this.isAnimating) return;

    const stateContainers = {
      input: this.step5InputState,
      result: this.step5ResultState,
      success: this.step5SuccessState,
    };

    const currentContainer = stateContainers[this.step5State];
    const targetContainer = stateContainers[targetState];

    if (!currentContainer || !targetContainer || this.step5State === targetState) return;

    this.isAnimating = true;

    // 当前状态淡出
    currentContainer.classList.add('is-fading-out');

    // 延迟后切换状态
    setTimeout(() => {
      // 隐藏当前状态
      currentContainer.classList.remove('is-fading-out');
      currentContainer.classList.add('yd-hidden');

      // 显示目标状态
      targetContainer.classList.remove('yd-hidden');
      targetContainer.classList.add('is-fading-in');

      // 更新状态
      this.step5State = targetState;

      // 移除淡入类
      setTimeout(() => {
        targetContainer.classList.remove('is-fading-in');
        this.isAnimating = false;
      }, 500);
    }, 300);
  }

  updateProgress(step) {
    this.cards.forEach((card) => {
      const progressBars = card.querySelectorAll('.ergo-360-quiz__progress > div');
      progressBars.forEach((bar, index) => {
        if (index < step) {
          bar.classList.remove('yd-bg-black/10');
          bar.classList.add('yd-bg-[#33363e]');
        } else {
          bar.classList.remove('yd-bg-[#33363e]');
          bar.classList.add('yd-bg-black/10');
        }
      });
    });
  }

  // ===== 结果计算 =====

  calculateResult() {
    // Q2 选项值 → 曲线结果映射
    // A(1): Neck/Shoulders → neck
    // B(2): Lower Back → spine
    // C(3): Hips/Tailbone → hips
    // D(4): Legs/Feet → legs
    // E(5): Eyes/Headaches → neck
    const Q2_TO_CURVE = {
      '1': 'neck',
      '2': 'spine',
      '3': 'hips',
      '4': 'legs',
      '5': 'neck'
    };

    // 获取 Q2 答案（step2）
    const q2Answer = this.answers.step2;

    // 返回对应曲线，默认 spine
    return Q2_TO_CURVE[q2Answer] || 'spine';
  }

  displayResult(resultKey) {
    const resultData = this.resultsData[resultKey];
    if (!resultData) return;

    // 更新所有结果卡片中的显示
    const resultIcons = this.container.querySelectorAll('[data-result-icon]');
    const resultTexts = this.container.querySelectorAll('[data-result-text]');
    const resultDescriptions = this.container.querySelectorAll('[data-result-description]');

    resultIcons.forEach((icon) => {
      icon.src = resultData.icon;
      icon.alt = resultData.label;
    });

    resultTexts.forEach((text) => {
      text.textContent = resultData.label;
    });

    resultDescriptions.forEach((desc) => {
      desc.textContent = resultData.description;
    });
  }

  // ===== 邮箱提交 =====

  async submitEmail() {
    const email = this.emailInput?.value?.trim();

    if (!email) {
      this.emailInput?.focus();
      return;
    }

    // 简单邮箱格式验证
    if (!this.isValidEmail(email)) {
      this.emailInput?.focus();
      return;
    }

    // 禁用按钮防止重复提交
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Sending...';
    }

    // 保存邮箱
    this.answers.email = email;

    // 提交到 Brevo API
    const success = await this.submitToBrevoAPI(email);

    if (success) {
      // 追踪事件（用于触发 Automation 邮件）
      await this.trackBrevoEvent(email);

      // GA4 埋点：Quiz 邮箱提交成功
      if (window.Ergo360Analytics) {
        window.Ergo360Analytics.quizEmailSubmit(this.currentResult);
      }

      // 切换到成功状态
      this.showStep5State('success');
    } else {
      // 恢复按钮状态
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.textContent = 'Receive full report';
      }
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async submitToBrevoAPI(email) {
    // 检查是否配置了代理 URL
    if (!this.brevoProxyUrl) {
      return true; // 不阻塞用户流程
    }

    // 构建联系人属性（使用文字内容，方便邮件阅读）
    const attributes = {
      QUIZ_POSTURE: this.answers.step1Text || '',
      QUIZ_PAIN_AREA: this.answers.step2Text || '',
      QUIZ_WORK_SETUP: this.answers.step3Text || '',
      QUIZ_ERGO_GOAL: this.answers.step4Text || '',
      QUIZ_HEIGHT: this.answers.height || null,
      QUIZ_WEIGHT: this.answers.weight || null,
      QUIZ_RESULT: this.currentResult?.toUpperCase() || ''
    };

    try {
      const response = await fetch(this.brevoProxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'createContact',
          email: email,
          attributes: attributes,
          listId: this.brevoListId
        })
      });

      const result = await response.json();
      return result.success;

    } catch (error) {
      return false;
    }
  }

  async trackBrevoEvent(email) {
    if (!this.brevoProxyUrl) return;

    try {
      await fetch(this.brevoProxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'trackEvent',
          email: email,
          eventName: 'ergo_quiz_completed',
          properties: { result: this.currentResult?.toUpperCase() || '' }
        })
      });
    } catch (error) {
      // 静默处理错误
    }
  }
}

// ===== 初始化 =====

document.addEventListener('DOMContentLoaded', () => {
  const quizContainers = document.querySelectorAll('.ergo-360-quiz');
  quizContainers.forEach((container) => {
    new Ergo360Quiz(container);
  });
});

// Shopify 主题编辑器支持
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.ergo-360-quiz');
    if (container) {
      new Ergo360Quiz(container);
    }
  });
}
